import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Child, MedicalHistory, fetchChildren, fetchMedicalHistories, fetchMedicalHistory, getMedicalHistoryPdfUrl } from '../api/client'
import { useAuthStore } from '../stores/auth'
import { EmptyStateBox } from './EmptyStateBox'

// Modal component for viewing medical history details
function MedicalHistoryModal({ 
  isOpen, 
  onClose, 
  record, 
  getChildName, 
  getBloodTypeLabel, 
  t 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  record: MedicalHistory | null;
  getChildName: (childId: number) => string;
  getBloodTypeLabel: (bloodType: string) => string;
  t: any;
}) {
  if (!isOpen || !record) return null;

  // Helper function to render yes/no value
  const renderYesNo = (value: boolean) => {
    return value ? 
      <span className="text-green-600 font-medium">{t('yes', { ns: 'medicalHistory' })}</span> : 
      <span className="text-red-600 font-medium">{t('no', { ns: 'medicalHistory' })}</span>;
  };

  // Helper function to render a section
  const renderSection = (title: string, content: React.ReactNode) => (
    <div className="mb-4 border-2 border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-blue-50 px-4 py-2 border-b-2 border-gray-200">
        <h3 className="font-bold text-blue-800">{title}</h3>
      </div>
      <div className="p-4 bg-white">
        {content}
      </div>
    </div>
  );

  // Helper function to safely render array data that might be objects or ids
  const renderArrayData = (data: any) => {
    if (!data) return '-';
    
    // If it's an array
    if (Array.isArray(data)) {
      // Check if it's an array of objects with name property
      if (data.length > 0 && typeof data[0] === 'object' && data[0] !== null && 'name' in data[0]) {
        return data.map(item => item.name).join(', ');
      }
      // Otherwise just join the array elements
      return data.join(', ');
    }
    
    // If it's a string, return as is
    return String(data);
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-4xl mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 w-10 h-10 bg-neobrutalism-red border-2 border-black rounded-full flex items-center justify-center shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all z-50"
          aria-label={t('close', { ns: 'common', defaultValue: 'Close' })}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="card-neobrutalism p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
          {/* Container for top buttons - stacks on mobile, row on sm+ */}
          <div className="flex flex-col sm:flex-row items-center gap-2 mb-4 sm:mb-6"> 
            <button
              onClick={() => {
                getMedicalHistoryPdfUrl(record.id)
                  .then(url => window.open(url, '_blank'))
                  .catch((error) => {
                    if (error.message && error.message.includes('still being generated')) {
                      alert(t('pdfGenerating', { ns: 'medicalHistory', defaultValue: 'PDF is being generated. Please try again in a few seconds.' }));
                    } else if (error.response && error.response.status === 404) {
                      alert(t('pdfNotFound', { ns: 'medicalHistory', defaultValue: 'PDF not found. Please try again later.' }));
                    } else {
                      alert(t('pdfDownloadError', { ns: 'medicalHistory', defaultValue: 'Error downloading PDF. Please try again later.' }));
                    }
                  });
              }}
              className="btn-neobrutalism bg-neobrutalism-blue text-white py-2 px-3 text-sm flex items-center justify-center w-full sm:w-auto flex-1 sm:flex-none sm:px-4" // Added justify-center and w-full for stacked mobile
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {t('downloadPdfButton', { ns: 'medicalHistory', defaultValue: 'Download PDF' })}
            </button>
          </div>

          {/* Basic Information Section */}
          {renderSection(t('sections.basicInformation', { ns: 'medicalHistory' }), (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="font-bold mb-1">{t('childLabel', { ns: 'medicalHistory' })}</div>
                <div className="break-words">{getChildName(record.child)}</div>
              </div>
              <div>
                <div className="font-bold mb-1">{t('yearLabel', { ns: 'medicalHistory' })}</div>
                <div>{record.year}</div>
              </div>
              <div>
                <div className="font-bold mb-1">{t('weightLabel', { ns: 'medicalHistory' })}</div>
                <div>{record.weight} kg</div>
              </div>
              <div>
                <div className="font-bold mb-1">{t('bloodTypeLabel', { ns: 'medicalHistory' })}</div>
                <div>{getBloodTypeLabel(record.blood_type)}</div>
              </div>
            </div>
          ))}

          {/* Vaccination Section */}
          {renderSection(t('sections.vaccination', { ns: 'medicalHistory' }), (
            <div>
              <div className="font-bold mb-1">{t('form.isVaccinated', { ns: 'medicalHistory' })}</div>
              <div className="mb-3">{renderYesNo(record.is_vaccinated)}</div>
              
              {!record.is_vaccinated && (
                <>
                  <div>
                    <div className="font-bold mb-1">{t('form.missingVaccines', { ns: 'medicalHistory' })}</div>
                    <div className="break-words">{renderArrayData(record.missing_vaccines)}</div>
                  </div>
                  {record.other_missing_vaccines && (
                    <div className="mt-3">
                      <div className="font-bold mb-1">{t('form.otherMissingVaccines', { ns: 'medicalHistory', defaultValue: 'Other Missing Vaccines' })}</div>
                      <div className="break-words">{record.other_missing_vaccines}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}

          {/* Known Diseases Section */}
          {renderSection(t('sections.knownDiseases', { ns: 'medicalHistory' }), (
            <div>
              <div className="font-bold mb-1">{t('form.hasKnownDisease', { ns: 'medicalHistory' })}</div>
              <div className="mb-3">{renderYesNo(record.has_any_known_disease)}</div>
              
              {record.has_any_known_disease && (
                <>
                  <div>
                    <div className="font-bold mb-1">{t('form.knownDiseases', { ns: 'medicalHistory' })}</div>
                    <div className="break-words">{renderArrayData(record.known_diseases)}</div>
                  </div>
                  {record.other_known_diseases && (
                    <div className="mt-3">
                      <div className="font-bold mb-1">{t('form.otherKnownDiseases', { ns: 'medicalHistory', defaultValue: 'Other Known Diseases' })}</div>
                      <div className="break-words">{record.other_known_diseases}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}

          {/* Allergies Section */}
          {renderSection(t('sections.allergies', { ns: 'medicalHistory' }), (
            <div>
              <div className="font-bold mb-1">{t('form.hasAllergies', { ns: 'medicalHistory' })}</div>
              <div className="mb-3">{renderYesNo(record.has_allergies)}</div>
              
              {record.has_allergies && (
                <>
                  <div>
                    <div className="font-bold mb-1">{t('form.allergies', { ns: 'medicalHistory' })}</div>
                    <div className="break-words">{renderArrayData(record.allergies)}</div>
                  </div>
                  {record.other_allergies && (
                    <div className="mt-3">
                      <div className="font-bold mb-1">{t('form.otherAllergies', { ns: 'medicalHistory', defaultValue: 'Other Allergies' })}</div>
                      <div className="break-words">{record.other_allergies}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}

          {/* Major Disease Section */}
          {renderSection(t('sections.majorDisease', { ns: 'medicalHistory' }), (
            <div>
              <div className="font-bold mb-1">{t('form.hasMajorDisease', { ns: 'medicalHistory' })}</div>
              <div className="mb-3">{renderYesNo(record.has_any_major_disease)}</div>
              
              {record.has_any_major_disease && (
                <div>
                  <div className="font-bold mb-1">{t('form.majorDisease', { ns: 'medicalHistory' })}</div>
                  <div className="break-words">{renderArrayData(record.major_disease)}</div>
                </div>
              )}
            </div>
          ))}

          {/* Psychological Disorders Section */}
          {renderSection(t('sections.psychologicalDisorders', { ns: 'medicalHistory' }), (
            <div>
              <div className="font-bold mb-1">{t('form.hasPsychologicalDisorder', { ns: 'medicalHistory' })}</div>
              <div className="mb-3">{renderYesNo(record.has_psychological_disorder)}</div>
              
              {record.has_psychological_disorder && (
                <>
                  <div>
                    <div className="font-bold mb-1">{t('form.psychologicalDisorders', { ns: 'medicalHistory' })}</div>
                    <div className="break-words">{renderArrayData(record.psychological_disorders)}</div>
                  </div>
                  {record.other_psychological_disorders && (
                    <div className="mt-3">
                      <div className="font-bold mb-1">{t('form.otherPsychologicalDisorders', { ns: 'medicalHistory', defaultValue: 'Other Psychological Disorders' })}</div>
                      <div className="break-words">{record.other_psychological_disorders}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}

          {/* Medication Section */}
          {renderSection(t('sections.medication', { ns: 'medicalHistory' }), (
            <div>
              <div className="font-bold mb-1">{t('form.isReceivingMedication', { ns: 'medicalHistory' })}</div>
              <div className="mb-3">{renderYesNo(record.is_receiving_medication)}</div>
              
              {record.is_receiving_medication && (
                <div>
                  <div className="font-bold mb-1">{t('form.medication', { ns: 'medicalHistory' })}</div>
                  <div className="break-words">{renderArrayData(record.medication)}</div>
                </div>
              )}
            </div>
          ))}

          {/* Development Disorders Section */}
          {renderSection(t('sections.developmentDisorders', { ns: 'medicalHistory' }), (
            <div>
              <div className="font-bold mb-1">{t('form.hasDevelopmentDisorder', { ns: 'medicalHistory' })}</div>
              <div className="mb-3">{renderYesNo(record.has_any_development_disorder)}</div>
              
              {record.has_any_development_disorder && (
                <>
                  <div>
                    <div className="font-bold mb-1">{t('form.developmentDisorders', { ns: 'medicalHistory' })}</div>
                    <div className="break-words">{renderArrayData(record.development_disorders)}</div>
                  </div>
                  {record.other_development_disorders && (
                    <div className="mt-3">
                      <div className="font-bold mb-1">{t('form.otherDevelopmentDisorders', { ns: 'medicalHistory', defaultValue: 'Other Development Disorders' })}</div>
                      <div className="break-words">{record.other_development_disorders}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}

          {/* Specialist Care Section */}
          {renderSection(t('sections.specialistCare', { ns: 'medicalHistory' }), (
            <div>
              <div className="font-bold mb-1">{t('form.isWatchedBySpecialist', { ns: 'medicalHistory' })}</div>
              <div className="mb-3">{renderYesNo(record.is_being_watched_by_specialist)}</div>
              
              {record.is_being_watched_by_specialist && (
                <div>
                  <div className="font-bold mb-1">{t('form.specialistContactInfo', { ns: 'medicalHistory' })}</div>
                  <div className="break-words">{renderArrayData(record.specialist_contact_info)}</div>
                </div>
              )}
            </div>
          ))}

          {/* Health Issues Section */}
          {renderSection(t('sections.healthIssues', { ns: 'medicalHistory' }), (
            <div>
              <div className="font-bold mb-1">{t('form.hasHealthIssues', { ns: 'medicalHistory' })}</div>
              <div className="mb-3">{renderYesNo(record.has_worth_mentioning_health_issues)}</div>
              
              {record.has_worth_mentioning_health_issues && (
                <div>
                  <div className="font-bold mb-1">{t('form.healthIssues', { ns: 'medicalHistory' })}</div>
                  <div className="break-words">{renderArrayData(record.worth_mentioning_health_issues)}</div>
                </div>
              )}
            </div>
          ))}

          {/* Risk Group Section */}
          {renderSection(t('sections.riskGroup', { ns: 'medicalHistory' }), (
            <div>
              <div className="font-bold mb-1">{t('form.belongsToRiskGroup', { ns: 'medicalHistory' })}</div>
              <div className="mb-3">{renderYesNo(record.belongs_to_risk_group)}</div>
              
              {record.belongs_to_risk_group && (
                <div>
                  <div className="font-bold mb-1">{t('form.riskGroupExplanation', { ns: 'medicalHistory' })}</div>
                  <div className="break-words">{renderArrayData(record.risk_group_explanation)}</div>
                </div>
              )}
            </div>
          ))}

          {/* Underlying Diseases Section */}
          {renderSection(t('sections.underlyingDiseases', { ns: 'medicalHistory' }), (
            <div>
              <div className="font-bold mb-1">{t('form.hasUnderlyingDisease', { ns: 'medicalHistory' })}</div>
              <div className="mb-3">{renderYesNo(record.has_underlying_disease)}</div>
              
              {record.has_underlying_disease && (
                <>
                  <div>
                    <div className="font-bold mb-1">{t('form.underlyingDiseases', { ns: 'medicalHistory' })}</div>
                    <div className="break-words">{renderArrayData(record.underlying_diseases)}</div>
                  </div>
                  {record.other_underlying_diseases && (
                    <div className="mt-3">
                      <div className="font-bold mb-1">{t('form.otherUnderlyingDiseases', { ns: 'medicalHistory', defaultValue: 'Other Underlying Diseases' })}</div>
                      <div className="break-words">{record.other_underlying_diseases}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function MedicalHistoryBox() {
  const { t } = useTranslation(['common', 'medicalHistory'])
  const navigate = useNavigate()
  const [medicalHistories, setMedicalHistories] = useState<MedicalHistory[]>([])
  const [children, setChildren] = useState<Child[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { token, parent } = useAuthStore()
  const [selectedRecord, setSelectedRecord] = useState<MedicalHistory | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      if (!token || !parent?.id) {
        setLoading(false)
        return
      }

      try {
        const [medicalHistoriesData, childrenData] = await Promise.all([
          fetchMedicalHistories(),
          fetchChildren()
        ])
        setMedicalHistories(medicalHistoriesData || [])
        setChildren(childrenData || [])
      } catch (err) {
        setError(t('errors.loadFailed', { ns: 'common' }))
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [token, parent, t])

  const handleAddClick = () => {
    navigate('/medical-history/add')
  }

  const handleViewClick = (record: MedicalHistory) => {
    setIsModalOpen(true)
    setSelectedRecord(record)
    
    // Fetch full details
    fetchMedicalHistory(record.id)
      .then(fullRecord => {
        setSelectedRecord(fullRecord)
      })
      .catch(error => {
        console.error('Error fetching medical history details:', error)
        setError(t('errors.loadFailed', { ns: 'common' }))
      })
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedRecord(null)
  }

  const getBloodTypeLabel = (bloodType: string) => {
    return bloodType // Could be expanded with translations if needed
  }

  const getChildName = (childId: number) => {
    const child = children.find(c => c.id === childId)
    return child ? `${child.first_name} ${child.last_name}` : `Child #${childId}`
  }

  if (loading) {
    return (
      <div className="card-neobrutalism h-full flex flex-col">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neobrutalism-accent"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="card-neobrutalism h-full flex flex-col">
      <div className="flex items-center mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2 text-neobrutalism-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
        <h2 className="text-2xl font-black text-neobrutalism-text">
          {t('title', { ns: 'medicalHistory' })}
        </h2>
      </div>

      {error && (
        <div className="bg-neobrutalism-red p-4 rounded-lg border-2 border-black mb-4">
          <p className="text-neobrutalism-text font-bold">{error}</p>
        </div>
      )}

      <div className="flex-grow mb-6">
        {medicalHistories.length === 0 ? (
          <EmptyStateBox
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            }
            text={t('noRecords', { ns: 'medicalHistory' })}
            bgColor="bg-blue-50"
            borderColor="border-blue-200"
            iconBgColor="bg-blue-100"
            iconColor="text-blue-600"
          />
        ) : (
          <ul className="space-y-4">
            {medicalHistories.map((record) => (
              <li key={record.id} className="hover:translate-y-[-2px] border-4 border-blue-100 p-4">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                    </div>
                    <div className="w-full">
                      <p className="font-bold text-lg text-gray-800">{getChildName(record.child)}</p>
                      <div className="block sm:flex sm:items-center sm:gap-2">
                        <p className="text-sm text-gray-600 font-medium mb-1 sm:mb-0">{t('yearLabel', { ns: 'medicalHistory' })}: {record.year}</p>
                      </div>
                    </div>
                  </div>
                  <div className="self-start sm:self-center mt-2 sm:mt-0 w-full sm:w-auto">
                    <button
                      onClick={() => handleViewClick(record)}
                      className="btn-neobrutalism bg-neobrutalism-blue text-center block w-full sm:w-auto py-2 px-4 sm:py-3 sm:px-6 text-sm sm:text-base"
                    >
                      {t('view', { ns: 'common' })}
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <button
        onClick={handleAddClick}
        className="btn-neobrutalism bg-neobrutalism-accent w-full text-center text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4"
      >
        {t('addNewButton', { ns: 'medicalHistory' })}
      </button>

      <MedicalHistoryModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        record={selectedRecord}
        getChildName={getChildName}
        getBloodTypeLabel={getBloodTypeLabel}
        t={t}
      />
    </div>
  )
}